import { BffApiClientMolecule } from "@/app/api-client/store"
import { NftSelectionMoleculeReatom } from "@/app/nft/sell/selection/nft-selection-molecule.reatom"
import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import type { CollectionId } from "@anaconda/core/nft-collection/types"
import { computed } from "@reatom/core"
import { useQueries, useQuery } from "@tanstack/react-query"
import { createScope, molecule, useMolecule } from "bunshi/react"
import { useMemo } from "react"
import { HighestCollectionOffersMoleculeReatom } from "./store.reatom"
import { getHighestCollectionOffersQueryParams } from "./utils"

export const HighestCollectionOfferMoleculeReatom = molecule((mol) => {
  const bffApiClient = mol(BffApiClientMolecule)
  const { selectedNftsAtom } = mol(NftSelectionMoleculeReatom)

  // Get unique collection IDs from selected NFTs
  const collectionIds = computed(() => {
    const selectedNfts = selectedNftsAtom()
    const ids = selectedNfts.map((nft) => nft.collection.id)
    // Return unique collection IDs
    return Array.from(new Set(ids))
  }, "collectionIds")

  // Return query params for each collection
  const queryParamsForCollections = computed(() => {
    const ids = collectionIds()
    return ids.map((collectionId) => ({
      collectionId,
      queryParams: {
        ...getHighestCollectionOffersQueryParams(bffApiClient, collectionId),
        refetchInterval: 5000,
      },
    }))
  }, "queryParamsForCollections")

  return {
    collectionIds,
    queryParamsForCollections,
  }
})

/**
 * Scope for collection IDs to fetch offers for
 */
export const collectionIdsOfferScope = createScope<CollectionId[]>([])

/**
 * React hooks for backward compatibility during migration
 * These hooks use HighestCollectionOffersMoleculeReatom which gets the collection ID from context
 */
export function useHighestCollectionOffers() {
  const { offersQueryParams } = useMolecule(
    HighestCollectionOffersMoleculeReatom,
  )

  const query = useQuery(offersQueryParams())

  return useMemo(() => {
    return query.data?.offers ?? []
  }, [query.data])
}

export function useHighestCollectionOffer() {
  const { unionAddress } = useMolecule(WalletConnectMoleculeReatom)
  const offers = useHighestCollectionOffers()

  return useMemo(() => {
    const userWallet = unionAddress()
    const filteredOffers = offers.filter(
      (offer) => offer.order.maker !== userWallet,
    )

    if (filteredOffers.length === 0) {
      return undefined
    }

    const currency = filteredOffers[0]?.price.currency
    const amount = filteredOffers[0]?.price.amount

    return { currency, amount }
  }, [offers, unionAddress])
}

/**
 * Export with "Molecule" suffix for backward compatibility
 */
export const HighestCollectionOfferMolecule =
  HighestCollectionOfferMoleculeReatom
